<template src="./checkPhoneNumber.html"></template>

<script lang="ts">
    import { defineComponent, ref, onMounted, nextTick } from 'vue';
    import { axiosRequests } from '@/services/axiosinstance';
    import { editUrl } from '@/services/helper';
    import { Routes } from '@/const';

    interface ComponentModel {
        newPhone: string,
        phoneMaskOptions: any,
        isSendCodeButtonDisabled: boolean,
        phoneMaskValidated: boolean,
        phoneErrorText: string,
        code: string,
        codeErrorText: string,
        showSendButton: boolean,
        showCodeInput: boolean,
        showCheckButton: boolean,
        isCheckCodeButtonDisabled: boolean
    }

    export default defineComponent({
        name: 'CheckPhoneNumber',
        props: ["clientId", "clubId", "prefilledPhone"],
        emits: ["close", "update-phone"],
        data(): ComponentModel {
            return {
                newPhone: this.prefilledPhone.length > 0 ? this.prefilledPhone.toString() : "+7(",
                phoneMaskOptions: {
                    mask: "+7 (###) ###-##-##"
                },
                isSendCodeButtonDisabled: false,
                phoneMaskValidated: true,
                phoneErrorText: '',
                code: '',
                codeErrorText: '',
                showSendButton: true,
                showCodeInput: false,
                showCheckButton: false,
                isCheckCodeButtonDisabled: false
            }
        },
        watch: {
            newPhone(newVal) {
                this.validatePhoneAgainstMask(newVal);
                this.startOverAgain();
            },
        },
        methods: {
            validatePhoneAgainstMask: function (phone) {
                const mask = this.phoneMaskOptions.mask;

                if (phone?.length === mask.length && !phone.includes("#")) {
                    this.phoneMaskValidated = true;
                    this.phoneErrorText = '';
                } else {
                    this.phoneMaskValidated = false;
                    this.phoneErrorText = 'check_phone_number.phone_error';
                }
            },
            startOverAgain: function(){
                this.showSendButton = true;
                this.showCodeInput = false;
                this.showCheckButton = false;
                this.phoneErrorText = '';
                this.codeErrorText = '';
            },
            codeSent: async function(){
                this.showSendButton = false;
                this.showCodeInput = true;
                this.showCheckButton = true;
                this.phoneErrorText = '';
                this.codeErrorText = '';

                await nextTick();
                const codeInput = this.$refs.codeInput as any;
                if (codeInput && codeInput.$el) {
                    const inputEl = (codeInput.$el as HTMLElement).querySelector('input');
                    if (inputEl && typeof inputEl.focus === 'function') {
                        inputEl.focus();
                    }
                }
            },
            codeConfirmed: function(){
                this.phoneErrorText = '';
                this.codeErrorText = '';
                this.$emit('update-phone', this.newPhone);
                this.close()
            },
            
            close: function () {
                this.$emit("close", false);
            },
            sendCode: async function () {
                this.codeErrorText = '';

                if (this.clientId === undefined) return;

                this.isSendCodeButtonDisabled = true;

                axiosRequests.post(editUrl(Routes.sendPhoneConfirmationCode), { clientId: this.clientId, clubId: this.clubId, phone: this.newPhone })
                    .then(() => this.codeSent())
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                                this.codeErrorText = this.$t(err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    )
                    .finally(() => this.isSendCodeButtonDisabled = false);
            },
            checkCode: async function(){
                this.codeErrorText = '';

                if (this.clientId === undefined) return;

                this.isCheckCodeButtonDisabled = true;

                const screenWidth = window.screen.width;
                const screenHeight = window.screen.height;
                const localTime = new Date().toString();
                const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

                const postData = {
                    phone: this.newPhone,
                    code: this.code,
                    screenDimensions: screenWidth + 'x' + screenHeight,
                    localTime,
                    timeZone
                };
                axiosRequests.post(editUrl(Routes.checkPhoneConfirmationCode), postData)
                    .then(() => this.codeConfirmed())
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                                this.codeErrorText = this.$t(err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    )
                    .finally(() => this.isCheckCodeButtonDisabled = false);
            },
            checkPressedButton: function (event: any) {
                if (event.keyCode == 27) this.close();
            }
        },
        setup() {
            const newPhoneInput = ref<any>(null);

            onMounted(() => {
                if (newPhoneInput.value) {
                    const inputEl = newPhoneInput.value.$el.querySelector('input');
                    if (inputEl) {
                    inputEl.focus();
                    }
                }
            });

            return { newPhoneInput };
        },
        mounted() {
            this.validatePhoneAgainstMask(this.prefilledPhone);
            window.addEventListener('keyup', this.checkPressedButton);
        },
        beforeUnmount() {
            window.removeEventListener('keyup', this.checkPressedButton);
        }

    });
</script>

<style scoped>
    .check_phone_number_wrapper {
        position: fixed;
        top: 0px;
        bottom: 0px;
        left: 0px;
        right: 0px;
        background-color: #b5b4b466;
        z-index: 9999;
    }

    .check_phone_number_body {
        background-color: white;
        position: absolute;
        top: 50%;
        max-width: 480px;
        width: max-content;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        border-radius: 24px;
        overflow: hidden;
        animation: show 0.6s linear;
    }

    .check_phone_number_title {
        font-size: 17px;
        letter-spacing: 1px;
        font-weight: 600;
        background-color: #f6f7f8;
        padding: 16px 24px;
    }

    .check_phone_number_row {
        margin-top: 12px;
        padding: 0px 24px;
    }

    .check_phone_number_buttons_wrapper {
        margin-top: 12px;
        display: flex;
        padding: 0px 24px;
        margin-bottom: 8px;
    }

    .button_space {
        margin-left: 16px;
    }

    .error_text{
        color: red;
        font-size: 12px;
        line-height: 14px;
    }


    @media(max-width: 500px) {
        .check_phone_number_row {
            width: 98%;
        }

        .check_phone_number_buttons_wrapper {
            display: block;
            margin-bottom: 16px;
        }

        .button_space {
            margin-left: 0px;
        }
    }
</style>